import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { TooltipBubble } from '../Tooltip';

export interface IFieldLabel {
    /** Visible label text. */
    title: React.ReactNode;
    /** Help text. When empty, no icon is rendered. */
    tooltipContent?: string;
    /** Element used for the label itself. Defaults to h1 to match existing markup. */
    as?: 'h1' | 'p' | 'label' | 'h3';
    /** Class for the label element. Defaults to the shared fieldHeading style. */
    className?: string;
    /** Rendered after the icon, e.g. the sr-only label RadioGroup needs. */
    children?: React.ReactNode;
    /** Width passed through to the bubble. */
    maxWidth?: string;
    /**
     * Smaller icon and tap target, for dense contexts such as the lender
     * card's column headings where a 44px target would break the grid.
     */
    compact?: boolean;
    /**
     * Marks the label with an asterisk. Field wrappers default this to
     * true, since nearly every field in the journey must be answered;
     * the handful of optional ones opt out explicitly.
     */
    required?: boolean;
}

export const testId = 'fieldLabel';

/**
 * A field label with an optional information icon.
 *
 * The icon is a button: click, tap, Enter or Space opens the help text, and
 * clicking or tapping anywhere else closes it. There is deliberately no hover
 * behaviour, so the interaction is identical on desktop and on a phone.
 */
export function FieldLabel({
    title,
    tooltipContent,
    as = 'h1',
    className = 'fieldHeading',
    children,
    maxWidth,
    compact = false,
    required = false,
}: IFieldLabel): JSX.Element {
    const content = tooltipContent ?? '';
    const hasContent = content !== '';

    const iconRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, bottom: 0 });

    const bubbleId = `${useId()}-tooltip`;
    const Label = as;

    const close = useCallback((): void => setOpen(false), []);

    const toggle = useCallback((): void => {
        setOpen((wasOpen) => {
            if (wasOpen) return false;
            const el = iconRef.current;
            if (el) {
                const rect = el.getBoundingClientRect();
                setPosition({
                    top: rect.top + window.scrollY,
                    left: rect.left + rect.width / 2 + window.scrollX,
                    bottom: rect.bottom + window.scrollY,
                });
            }
            return true;
        });
    }, []);

    // Dismiss on outside tap, Escape, scroll or resize.
    useEffect(() => {
        if (!open) return undefined;

        function onDocPointerDown(event: Event): void {
            if (iconRef.current?.contains(event.target as Node)) return;
            close();
        }
        function onKeyDown(event: KeyboardEvent): void {
            if (event.key === 'Escape') close();
        }

        document.addEventListener('pointerdown', onDocPointerDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);

        return () => {
            document.removeEventListener('pointerdown', onDocPointerDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open, close]);

    return (
        <div
            data-testid={testId}
            // The icon's own box is taller than a line of label text, so a
            // field with help sat 4px lower than one without and the two
            // stopped aligning side by side. A floor on every label row, icon
            // or not, keeps them level.
            className={`flex items-center gap-1 w-full ${
                compact ? '' : 'min-h-[24px] mb-1.5'
            }`}
        >
            <Label className={className}>
                {title}
                {required && (
                    // Decorative: the control itself carries the required
                    // semantics, so a screen reader is told twice otherwise.
                    <span aria-hidden='true' className='text-quaternary'>
                        {/* Non-breaking: the asterisk was wrapping onto a line
                            of its own when the label filled its column. */}
                        {'\u00a0*'}
                    </span>
                )}
            </Label>

            {hasContent && (
                // fieldHeading carries pb-1, so the label's text sits 2px
                // above the centre of its own box. Matching that padding here
                // lines the icon up with the words rather than with the box.
                <span className={`_flex items-center ${compact ? '' : 'pb-1'}`}>
                    <button
                        ref={iconRef}
                        type='button'
                        // Out of the tab order: on a long form it doubled the
                        // stops between fields. The help it opens is attached to
                        // the field itself, so nothing is lost by skipping it.
                        tabIndex={-1}
                        // -m-3/p-3 grows the tap target to 44px without moving the icon.
                        className={`${
                            compact ? '-m-1.5 p-1.5' : '-m-3 p-3'
                        } shrink-0 text-quaternary cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-secondary`}
                        aria-label='More information'
                        aria-expanded={open}
                        aria-describedby={open ? bubbleId : undefined}
                        onClick={toggle}
                    >
                        <InformationCircleIcon
                            height={compact ? 14 : 20}
                            width={compact ? 14 : 20}
                        />
                    </button>
                </span>
            )}

            {children}

            {hasContent &&
                open &&
                createPortal(
                    <div id={bubbleId} role='tooltip'>
                        <TooltipBubble
                            content={content}
                            maxWidth={maxWidth}
                            top={position.top}
                            left={position.left}
                            bottom={position.bottom}
                        />
                    </div>,
                    document.body
                )}
        </div>
    );
}
